import { CONTROL_ID_ATTRIBUTE_NAME, Control } from "Controls/Control";
import { CssClass, DisplayModes } from "Enums";
import { ControlTemplate } from "Controls/Entities";
import { DatabaseImageService } from "Service";
import { DataRequestOption, DatabaseImageResult } from "Service/Client";
import { IDataRow } from "Entities";
import { IControl } from "./Interfaces";

/**
 * Control for showing images stored in the database.
 *
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of an DatabaseImage with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class DatabaseImage extends Control {
    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-databaseimage";

    protected isBlockElement = false;
    
    /**
     * @hidden
     */
    protected defaultEditorElement = "<div />";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {}

    /**
     * Set loaded image.
     * @param image Image data from server.
     * @hidden
     */
    public setImage(image: DatabaseImageResult) {
        if (image && image.Content) {
            const img = $(`<img src="data:${image.ContentType};base64,${image.Content}"/>`);
            if (this.controlDefinition.Width) {
                img.css("max-width", this.controlDefinition.Width);
            }
            if (this.controlDefinition.Height) {
                img.css("max-height", this.controlDefinition.Height);
            }
            this.jQueryElement.append(img);
        }
    }

    /**
     * Get data request info for server.
     * @returns
     * @hidden
     */
    public getDataRequest(): DataRequestOption {
        if (this.controlDefinition.CommandID) {
            return {
                ControlID: this.controlDefinition.ControlID,
            };
        }
        return undefined;
    }

    /**
     * Logic to load multiple images and set it to the controls
     * @param controls
     * @param databaseImageService
     * @param gridID
     * @param dataRow
     * @hidden
     */
    public static loadMultiple(controls: IControl[], databaseImageService: DatabaseImageService, gridID: number, dataRow: IDataRow) {
        const options: DataRequestOption[] = [];

        const controlDict: { [controlId: number]: DatabaseImage } = {};
        for (const control of controls) {
            const dbImage = <DatabaseImage>control;
            if (dbImage.setImage && control.getControlDefinition().CommandID != null) {
                options.push(dbImage.getDataRequest());
                controlDict[control.getControlId()] = dbImage;
            }
        }

        if (options.length > 0) {
            databaseImageService.getAll(gridID, options, dataRow).then((data) => {
                for (let i = 0, len = options.length; i < len; i++) {
                    let ctrlData = data[options[i].ControlID];
                    if (ctrlData != null) {
                        if (!ctrlData.Error) {
                            let ctl = controlDict[options[i].ControlID];
                            let dbImage = <DatabaseImage>ctl;
                            if (dbImage.setImage) {
                                dbImage.setImage(ctrlData);
                            }
                        }
                    }
                }
            });
        }
    }

    /**
     * Unused.
     * @hidden
     */
    public getValue(): string {
        return null;
    }

    /**
     * Unused.
     * @param value
     * @hidden
     */
    public setValue(value: string) {}

    /**
     * Gets template for showing tooltips.
     * @param bindingField Bound field in the datarow.
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return "";
    }

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField?: string): ControlTemplate {
        let controlTemplate = new ControlTemplate();
        let element: JQuery;
        if (displayMode == DisplayModes.Display || (displayMode == DisplayModes.Edit && !this.controlDefinition.OnEditEditable)) {
            if (this.controlDefinition.CommandID) {
                element = $(`<div class="${this.dmsControlClass} ${CssClass.databaseImage}" />`);
                element.attr(CONTROL_ID_ATTRIBUTE_NAME, this.controlDefinition.ControlID);
            } else {
                element = $(`<img src="data:#= get('${bindingField}ContentType') #;base64,#= get('${bindingField}') #"/>`);
            }
            if (this.controlDefinition.Width) {
                element.css("max-width", this.controlDefinition.Width);
            }
            if (this.controlDefinition.Height) {
                element.css("max-height", this.controlDefinition.Height);
            }
        } else {
            element = $("<div/>");
        }

        controlTemplate.template = element[0].outerHTML;
        controlTemplate.element = element;
        controlTemplate.editable = false;
        return controlTemplate;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
    }
}
